import { useEffect, useState } from "react";
import {
  BookOpen,
  Bookmark,
  Building2,
  CircleDollarSign,
  FileText,
  Gauge,
  Handshake,
  MessageSquareHeart,
  GraduationCap,
  HelpCircle,
  Home,
  KanbanSquare,
  KeyRound,
  LayoutDashboard,
  LayoutGrid,
  Library,
  LineChart,
  Lock,
  Megaphone,
  MessageSquareText,
  Newspaper,
  Plus,
  Send,
  Settings,
  Share2,
  ShieldCheck,
  Sparkles,
  Trash2,
  UserCheck,
  UserRound,
  UserRoundSearch,
  Users,
  UsersRound,
} from "lucide-react";

import AppSidebar, { type NavItem, type NavSection } from "@/components/AppSidebar";
import { SAVED_FILTERS_CHANGED_EVENT, renameSavedFilter, type SavedFilter } from "@/components/ListingFilters";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { isOrgAdmin } from "@/lib/roles";

export default function AdminSidebar() {
  const { user } = useAuth();
  const [interestCount, setInterestCount] = useState(0);
  const [newListingCount, setNewListingCount] = useState(0);
  const [newUserCount, setNewUserCount] = useState(0);
  const [newAdvisorCount, setNewAdvisorCount] = useState(0);
  const [newBuyerCount, setNewBuyerCount] = useState(0);
  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([]);

  const loadSaved = () => api.get<SavedFilter[]>("/saved-filters/").then(setSavedFilters).catch(() => {});

  useEffect(() => {
    if (!user) return;
    api.get<{ count: number }>("/interests/unread-count").then((d) => setInterestCount(d.count)).catch(() => {});
    api.get<{ count: number }>("/business-listings/new-count").then((d) => setNewListingCount(d.count)).catch(() => {});
    api.get<{ count: number }>("/users/new-count").then((d) => setNewUserCount(d.count)).catch(() => {});
    api.get<{ count: number }>("/directory/advisors/new-count").then((d) => setNewAdvisorCount(d.count)).catch(() => {});
    api.get<{ count: number }>("/directory/buyers/new-count").then((d) => setNewBuyerCount(d.count)).catch(() => {});
    loadSaved();
    window.addEventListener(SAVED_FILTERS_CHANGED_EVENT, loadSaved);
    return () => window.removeEventListener(SAVED_FILTERS_CHANGED_EVENT, loadSaved);
  }, [user]);

  const deleteSavedFilter = (id: string) => {
    api.delete(`/saved-filters/${id}`)
      .then(() => {
        loadSaved();
        window.dispatchEvent(new Event(SAVED_FILTERS_CHANGED_EVENT));
      })
      .catch(() => {});
  };

  // Platform-wide surfaces: shown to BusinessLocating's admins only. The API
  // 403s for org_admins regardless — hiding is courtesy, not enforcement.
  const platformItems: NavItem[] = isOrgAdmin(user)
    ? []
    : [
        { label: "Announcements", module: "announcements", to: "/admin/announcements", icon: Sparkles },
        { label: "Release Notes", module: "release_notes", to: "/admin/release-notes", icon: FileText },
      ];

  const sections: NavSection[] = [
    {
      heading: "Overview",
      items: [{ label: "Home", to: "/admin/home", icon: Home }],
    },
    {
      // The reporting surfaces, kept apart from Home: these three answer
      // "how is the business doing", while Home is the way back to the console.
      heading: "Dashboard",
      items: [
        { label: "Listings", module: "all_listings", to: "/admin/all-listings", icon: LayoutGrid },
        // Both are shown to an org_admin too — the API scopes each to their own
        // organization, so a tenant admin sees their own revenue and their own
        // usage rather than the platform's.
        { label: "Finance", module: "finance_dashboard", to: "/admin/finance", icon: LineChart },
        {
          // Expandable: the same page split by audience. `to` is the parent
          // route, which redirects to Buyers — it still has to be a real path
          // because AppSidebar folds it into the active-match list.
          label: "Utilization",
          module: "utilization_dashboard",
          to: "/admin/utilization",
          icon: Gauge,
          children: [
            { label: "Buyers", to: "/admin/utilization/buyers", icon: UsersRound },
            { label: "BL Advisors", to: "/admin/utilization/advisors", icon: UserCheck },
          ],
        },
      ],
    },
    {
      heading: "Manage",
      items: [
        { label: "Moderation", module: "moderation", to: "/admin/moderation", icon: Sparkles, badgeCount: newListingCount, onClick: () => setNewListingCount(0) },
        { label: "My Listings", module: "my_listings", to: "/admin/listings", icon: LayoutDashboard },
        { label: "New Listing", module: "new_listing", to: "/admin/new-listing", icon: Plus },
        { label: "Deleted Listings", module: "deleted_listings", to: "/admin/listings/deleted", icon: Trash2 },
        // Both admin tiers — the API scopes an org_admin to their own org's shares.
        { label: "Shared Listings", module: "shared_listings", to: "/admin/shared-listings", icon: Send },
        { label: "Interests", module: "interests", to: "/admin/interests", icon: MessageSquareHeart, badgeCount: interestCount, onClick: () => setInterestCount(0) },
        { label: "Buyer Activity", module: "buyer_activity", to: "/admin/buyer-activity", icon: Lock },
        { label: "My Assignments", module: "assignments", to: "/admin/assignments", icon: UserCheck },
        { label: "Deal CRM", module: "deal_crm", to: "/admin/pipeline", icon: KanbanSquare },
      ],
    },
    {
      heading: "Directory",
      items: [
        { label: "All BL Advisors", module: "advisor_directory", to: "/admin/advisors", icon: UserCheck, badgeCount: newAdvisorCount, onClick: () => setNewAdvisorCount(0) },
        { label: "All Buyers", module: "buyer_directory", to: "/admin/buyers", icon: UsersRound, badgeCount: newBuyerCount, onClick: () => setNewBuyerCount(0) },
      ],
    },
    {
      // The two inbound-lead queues. Kept as sibling items rather than one
      // expandable parent so each keeps its own super-admin module switch —
      // child links are not module-filtered (see AppSidebar), so nesting them
      // would have put both behind a single toggle.
      heading: "Leads (Opportunities)",
      items: [
        { label: "Seller Leads", module: "seller_contacts", to: "/admin/seller-contacts", icon: Handshake },
        { label: "Buyer Leads", module: "buyer_leads", to: "/admin/buyer-leads", icon: UserRoundSearch },
        // Pre-qualification inquiries from the buyer Funding Marketplace. Same
        // module key as the buyer form — the registry already covers both.
        { label: "Funding Applications", module: "funding_marketplace", to: "/admin/funding-applications", icon: CircleDollarSign },
      ],
    },
    {
      heading: "Platform",
      items: [
        { label: "User Management", module: "user_management", to: "/admin/users", icon: Users, badgeCount: newUserCount, onClick: () => setNewUserCount(0) },
        { label: "Buyer Feedback", module: "buyer_feedback", to: "/admin/feedback", icon: MessageSquareText },
        ...platformItems,
        { label: "BL Advisor Referrals", module: "referrals", to: "/admin/referrals", icon: Share2 },
        {
          label: "Resource",
          module: "resources",
          to: "/resources",
          icon: BookOpen,
          children: [
            // The reading library — Glossary, FAQ, News and Press — is the same
            // for both kinds of admin: the API grants an org admin every
            // published resource (the org-admin branch of _caller_role_ids) and
            // previews everything for a platform admin.
            { label: "Glossary", to: "/resources/glossary", icon: BookOpen },
            { label: "FAQ", to: "/resources/faq", icon: HelpCircle },
            { label: "Latest News & Articles", to: "/resources/news", icon: Newspaper },
            { label: "Press & Media", to: "/resources/press", icon: Megaphone },
            // Mastermind and authoring are platform-admin only. _STAFF_ROLES
            // server-side is bl_advisor + admin, and require_resource_author
            // refuses an org admin, so either link would only walk them into a
            // 403.
            ...(isOrgAdmin(user)
              ? []
              : [
                  { label: "Mastermind", to: "/resources/mastermind", icon: GraduationCap },
                  { label: "Manage Content", to: "/admin/resource-content", icon: Library },
                ]),
          ],
        },
        { label: "Coming Soon Deals", module: "coming_soon_deals", to: "/admin/lead-pipeline", icon: Building2 },
      ],
    },
    {
      heading: "Account",
      items: [
        {
          label: "Settings",
          to: "/admin/settings",
          icon: Settings,
          children: [
            { label: "My Profile", to: "/admin/settings/profile", icon: UserRound },
            { label: "Password & Security", to: "/admin/settings/security", icon: KeyRound },
            { label: "Documents", to: "/admin/settings/documents", icon: FileText },
          ],
        },
      ],
    },
  ];

  // Portal-admin-only tools.
  if (user?.email === "admin@portal.com") {
    sections.splice(sections.length - 1, 0, {
      heading: "Tools",
      items: [{ label: "NDA", module: "nda_management", to: "/admin/upload-nda", icon: ShieldCheck }],
    });
  }

  if (savedFilters.length > 0) {
    // Anchored AFTER the Dashboard block rather than at a fixed index: these
    // are saved views of All Listings, so they belong next to the reporting
    // group. A hardcoded 1 used to mean that, but once Home was split into its
    // own Overview section it would have wedged Saved Filters between Home and
    // Dashboard instead.
    const dashboardIdx = sections.findIndex((s) => s.heading === "Dashboard");
    sections.splice(dashboardIdx >= 0 ? dashboardIdx + 1 : 1, 0, {
      heading: "Saved Filters",
      items: savedFilters.map((f) => ({
        label: f.name,
        to: `/admin/all-listings?savedFilter=${f.id}`,
        icon: Bookmark,
        onDelete: () => deleteSavedFilter(f.id),
        onRename: (name: string) => { renameSavedFilter(f.id, name).catch(() => {}); },
      })),
    });
  }

  return <AppSidebar sections={sections} homeUrl={"/admin/home"} storageKey="adminSidebarCollapsed" logoutPath={"/admin/sign-in"} />;
}
